import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle,
  Download, RefreshCw, X, ArrowRight, ArrowLeft, Search,
  Check, Info, Shield
} from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';
import toast from '../../utils/toast';

const BulkLICModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Effective recovery period defaults (defaults to current month and year)
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const [effectiveMonth, setEffectiveMonth] = useState(currentMonth);
  const [effectiveYear, setEffectiveYear] = useState(currentYear);

  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [previewFilter, setPreviewFilter] = useState('all'); // all, valid, duplicate, invalid
  const [previewSearch, setPreviewSearch] = useState('');

  // Import results
  const [importResult, setImportResult] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const fileInputRef = useRef(null);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => currentYear - 2 + i);

  if (!isOpen) return null;

  const resetState = () => {
    setStep(1);
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    setError('');
    setPreviewFilter('all');
    setPreviewSearch('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const validExts = ['.xlsx', '.xls'];
    const lowerName = file.name.toLowerCase();
    const isValid = validExts.some(ext => lowerName.endsWith(ext));
    if (!isValid) {
      setError('Invalid file type. Please upload an Excel file (.xlsx or .xls).');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File size exceeds the 20MB limit.');
      return;
    }
    setError('');
    setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  // Download Sample Template
  const handleDownloadTemplate = async () => {
    try {
      setError('');
      const res = await fetch(buildApiUrl('/api/v1/lic/bulk-upload/template'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!res.ok) {
        throw new Error('Failed to download template');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lic_policy_upload_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message || 'Error downloading template');
    }
  };

  // Upload and Parse Preview
  const handlePreview = async () => {
    if (!selectedFile) {
      setError('Please choose an Excel file to upload');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('effective_month', effectiveMonth);
      formData.append('effective_year', effectiveYear);

      const res = await fetch(buildApiUrl('/api/v1/lic/bulk-upload/preview'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to preview Excel file');
      }

      setPreviewData(data);
      setStep(2);
    } catch (err) {
      const msg = err.message || 'Error parsing Excel file';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Confirm and Execute Import
  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    const validCount = previewData?.summary?.valid_count ?? 0;
    if (validCount === 0) {
      setError('There are no valid records to import.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('effective_month', effectiveMonth);
      formData.append('effective_year', effectiveYear);

      const res = await fetch(buildApiUrl('/api/v1/lic/bulk-upload/import'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to import LIC policies');
      }

      setImportResult(data);
      setStep(3);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const msg = err.message || 'Error executing bulk import';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Download Error / Skipped Report
  const handleDownloadErrorReport = async () => {
    const recordsToExport = [];

    if (importResult) {
      if (importResult.failed_records?.length > 0) {
        recordsToExport.push(...importResult.failed_records);
      }
      if (importResult.duplicates?.length > 0) {
        recordsToExport.push(...importResult.duplicates);
      }
    } else if (previewData && previewData.rows) {
      const issues = previewData.rows.filter(r => r.status !== 'VALID');
      issues.forEach(r => {
        recordsToExport.push({
          row_number: r.row_number,
          emp_code: r.emp_code,
          employee_name: r.employee_name,
          lic_number: r.lic_number,
          monthly_premium: r.monthly_premium,
          reason: (r.errors || []).join('; ') || (r.status === 'DUPLICATE' ? 'Duplicate policy' : 'Invalid row')
        });
      });
    }

    if (recordsToExport.length === 0) {
      return;
    }

    setDownloadingReport(true);
    try {
      const res = await fetch(buildApiUrl('/api/v1/lic/bulk-upload/export-errors'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(recordsToExport)
      });

      if (!res.ok) {
        throw new Error('Failed to generate error report');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lic_import_exceptions_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Exception report downloaded successfully');
    } catch (err) {
      const msg = err.message || 'Error downloading error report';
      setError(msg);
      toast.error(msg);
    } finally {
      setDownloadingReport(false);
    }
  };

  // Filter preview rows
  const getFilteredRows = () => {
    if (!previewData || !previewData.rows) return [];
    return previewData.rows.filter(row => {
      if (previewFilter === 'valid' && row.status !== 'VALID') return false;
      if (previewFilter === 'duplicate' && row.status !== 'DUPLICATE') return false;
      if (previewFilter === 'invalid' && row.status !== 'INVALID') return false;

      if (previewSearch.trim()) {
        const q = previewSearch.toLowerCase();
        const empCode = (row.emp_code || '').toLowerCase();
        const empName = (row.employee_name || '').toLowerCase();
        const policyNo = (row.lic_number || '').toLowerCase();
        return empCode.includes(q) || empName.includes(q) || policyNo.includes(q);
      }
      return true;
    });
  };

  const filteredPreviewRows = getFilteredRows();

  // Summary counts
  const totalRowsCount = previewData?.summary?.total_rows ?? 0;
  const validRowsCount = previewData?.summary?.valid_count ?? 0;
  const duplicateRowsCount = previewData?.summary?.duplicate_count ?? 0;
  const invalidRowsCount = previewData?.summary?.invalid_count ?? 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden border border-gray-100 flex flex-col max-h-[92vh]"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50/50 via-white to-indigo-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-blue-600 rounded-xl text-white shadow-sm">
              <UploadCloud className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Bulk Upload LIC Policies</h2>
              <p className="text-xs text-gray-500">
                Import multi-employee LIC statement spreadsheet with automated validation and exception handling
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-3 bg-gray-50/60 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center space-x-6 text-xs font-semibold">
            <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>1</span>
              <span>Upload File & Period</span>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
            <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>2</span>
              <span>Preview & Verify</span>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
            <div className={`flex items-center space-x-2 ${step >= 3 ? 'text-emerald-600' : 'text-gray-400'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step >= 3 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>3</span>
              <span>Import Summary</span>
            </div>
          </div>
          {selectedFile && (
            <div className="text-xs text-gray-500 font-medium">
              File: <span className="text-gray-800 font-semibold">{selectedFile.name}</span>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center space-x-3 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-red-600" />
            <span className="flex-1 font-medium">{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* STEP 1: Upload & Defaults */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Template Download Card */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-100 text-blue-700 rounded-lg mt-0.5">
                    <FileSpreadsheet className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">Need the standard LIC statement template?</h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Download the formatted Excel template with Code No, Employee Name, Policy No, and LIC Amount columns.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="inline-flex items-center px-3.5 py-2 bg-white border border-blue-200 hover:border-blue-300 text-blue-700 hover:bg-blue-50/50 rounded-lg text-xs font-semibold shadow-sm transition whitespace-nowrap"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download Template (.xlsx)
                </button>
              </div>

              {/* Effective Recovery Period */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Info className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Default Effective Recovery Month
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Effective Month</label>
                    <select
                      value={effectiveMonth}
                      onChange={(e) => setEffectiveMonth(parseInt(e.target.value))}
                      className="w-full border border-gray-200 bg-white rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      {months.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Effective Year</label>
                    <select
                      value={effectiveYear}
                      onChange={(e) => setEffectiveYear(parseInt(e.target.value))}
                      className="w-full border border-gray-200 bg-white rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      {years.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  * Note: Applied to all uploaded policies that do not specify a separate effective month.
                </p>
              </div>

              {/* File Dropzone */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Select LIC Statement Spreadsheet (.xlsx, .xls)
                </label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition flex flex-col items-center justify-center ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50/50'
                      : selectedFile
                      ? 'border-emerald-300 bg-emerald-50/20'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleFileChange}
                  />

                  {selectedFile ? (
                    <div className="space-y-2">
                      <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                        <FileSpreadsheet className="w-6 h-6" />
                      </div>
                      <div className="font-semibold text-gray-900 text-sm">{selectedFile.name}</div>
                      <div className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB — Click or drag to replace
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto">
                        <UploadCloud className="w-6 h-6" />
                      </div>
                      <div className="font-semibold text-gray-800 text-sm">
                        Click to browse or drag and drop your Excel sheet here
                      </div>
                      <div className="text-xs text-gray-400">
                        Supports statement format with multiple policies per employee, subtotal rows, and grand totals
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Preview & Validation */}
          {step === 2 && previewData && (
            <div className="space-y-5">
              {/* Summary KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase">Total Rows Detected</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">{totalRowsCount}</div>
                </div>
                <div className="bg-emerald-50/80 border border-emerald-100 rounded-xl p-3">
                  <div className="text-[11px] font-semibold text-emerald-700 uppercase flex items-center justify-between">
                    <span>Valid Policies</span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <div className="text-xl font-bold text-emerald-700 mt-1">{validRowsCount}</div>
                </div>
                <div className="bg-amber-50/80 border border-amber-100 rounded-xl p-3">
                  <div className="text-[11px] font-semibold text-amber-700 uppercase flex items-center justify-between">
                    <span>Duplicate Warnings</span>
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  </div>
                  <div className="text-xl font-bold text-amber-700 mt-1">{duplicateRowsCount}</div>
                </div>
                <div className="bg-red-50/80 border border-red-100 rounded-xl p-3">
                  <div className="text-[11px] font-semibold text-red-700 uppercase flex items-center justify-between">
                    <span>Invalid Rows</span>
                    <XCircle className="w-3.5 h-3.5 text-red-600" />
                  </div>
                  <div className="text-xl font-bold text-red-700 mt-1">{invalidRowsCount}</div>
                </div>
              </div>

              {/* Exception notice */}
              {(invalidRowsCount > 0 || duplicateRowsCount > 0) && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl flex items-start space-x-2 text-xs">
                  <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Safe Exception Handling:</strong> Invalid and duplicate policies will be{' '}
                    <span className="font-bold underline">safely skipped</span> during import without interrupting or
                    rolling back valid records.
                  </div>
                </div>
              )}

              {/* Search & Filter Toolbar */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-1.5 bg-gray-100 p-1 rounded-lg text-xs font-semibold">
                  <button
                    onClick={() => setPreviewFilter('all')}
                    className={`px-3 py-1.5 rounded-md transition ${
                      previewFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    All ({totalRowsCount})
                  </button>
                  <button
                    onClick={() => setPreviewFilter('valid')}
                    className={`px-3 py-1.5 rounded-md transition ${
                      previewFilter === 'valid' ? 'bg-emerald-600 text-white shadow-sm' : 'text-emerald-700 hover:bg-emerald-50'
                    }`}
                  >
                    Valid ({validRowsCount})
                  </button>
                  <button
                    onClick={() => setPreviewFilter('duplicate')}
                    className={`px-3 py-1.5 rounded-md transition ${
                      previewFilter === 'duplicate' ? 'bg-amber-600 text-white shadow-sm' : 'text-amber-700 hover:bg-amber-50'
                    }`}
                  >
                    Duplicates ({duplicateRowsCount})
                  </button>
                  <button
                    onClick={() => setPreviewFilter('invalid')}
                    className={`px-3 py-1.5 rounded-md transition ${
                      previewFilter === 'invalid' ? 'bg-red-600 text-white shadow-sm' : 'text-red-700 hover:bg-red-50'
                    }`}
                  >
                    Invalid ({invalidRowsCount})
                  </button>
                </div>

                <div className="relative flex-1 sm:max-w-xs">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="Search by code, name, or policy..."
                    value={previewSearch}
                    onChange={(e) => setPreviewSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm max-h-80 overflow-y-auto">
                <table className="min-w-full text-xs text-left">
                  <thead className="bg-gray-50 text-gray-600 font-bold border-b border-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2.5 text-center">Row</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-3 py-2.5">Employee</th>
                      <th className="px-3 py-2.5">Policy Number</th>
                      <th className="px-3 py-2.5 text-right">Monthly Premium</th>
                      <th className="px-3 py-2.5">Effective Month</th>
                      <th className="px-3 py-2.5">Remarks / Issues</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700 bg-white">
                    {filteredPreviewRows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No records match the selected filter.
                        </td>
                      </tr>
                    ) : (
                      filteredPreviewRows.map((r, i) => {
                        const issuesList = r.errors || [];
                        return (
                          <tr key={i} className="hover:bg-gray-50/60 transition">
                            <td className="px-3 py-2 text-center font-mono text-gray-400 font-medium">
                              {r.row_number}
                            </td>
                            <td className="px-3 py-2">
                              {r.status === 'VALID' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <Check className="w-3 h-3 mr-0.5" /> VALID
                                </span>
                              )}
                              {r.status === 'DUPLICATE' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                  <AlertTriangle className="w-3 h-3 mr-0.5" /> DUPLICATE
                                </span>
                              )}
                              {r.status === 'INVALID' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200">
                                  <X className="w-3 h-3 mr-0.5" /> INVALID
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-gray-900">
                                {r.employee_name || (r.employee_id ? 'Resolved' : <span className="text-red-500 font-normal">Not Found</span>)}
                              </div>
                              <div className="text-[11px] font-mono text-gray-500">
                                {r.emp_code || '-'}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-semibold text-gray-800 flex items-center">
                                <Shield className="w-3 h-3 mr-1 text-blue-500" />
                                {r.lic_number || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-gray-900">
                              {r.monthly_premium != null
                                ? `₹${Number(r.monthly_premium).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                                : '-'}
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-600">
                              {r.effective_from_month || '-'}
                            </td>
                            <td className="px-3 py-2 max-w-xs">
                              {issuesList.length > 0 ? (
                                <div className="text-[11px] text-red-600 space-y-0.5 font-medium">
                                  {issuesList.map((iss, idx) => (
                                    <div key={idx} className="flex items-start space-x-1">
                                      <span>•</span>
                                      <span>{iss}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : r.status === 'DUPLICATE' ? (
                                <span className="text-[11px] text-amber-700 font-medium">
                                  Active policy already exists
                                </span>
                              ) : (
                                <span className="text-[11px] text-emerald-600 font-medium">Ready to import</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 3: Import Results */}
          {step === 3 && importResult && (
            <div className="space-y-6 py-2">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Bulk Import Completed</h3>
                <p className="text-xs text-gray-500">
                  Employee LIC policies have been imported and updated in the system.
                </p>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <div className="text-xs font-bold text-emerald-700 uppercase">Policies Created</div>
                  <div className="text-2xl font-bold text-emerald-700 mt-1">
                    {importResult.created_count ?? 0}
                  </div>
                  <div className="text-[11px] text-emerald-600 mt-0.5">Successfully inserted</div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <div className="text-xs font-bold text-amber-700 uppercase">Duplicates Skipped</div>
                  <div className="text-2xl font-bold text-amber-700 mt-1">
                    {importResult.skipped_duplicates ?? 0}
                  </div>
                  <div className="text-[11px] text-amber-600 mt-0.5">Existing active policies</div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <div className="text-xs font-bold text-red-700 uppercase">Exceptions / Failed</div>
                  <div className="text-2xl font-bold text-red-700 mt-1">
                    {importResult.failed_count ?? 0}
                  </div>
                  <div className="text-[11px] text-red-600 mt-0.5">Skipped without error</div>
                </div>
              </div>

              {/* Download error report if any exceptions */}
              {((importResult.failed_count > 0) || (importResult.skipped_duplicates > 0) || (importResult.failed_records?.length > 0)) && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-xs text-gray-600">
                    <strong>Exception Audit:</strong> Download an Excel report containing all skipped and failed
                    rows with detailed explanations.
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadErrorReport}
                    disabled={downloadingReport}
                    className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-semibold shadow-sm transition whitespace-nowrap"
                  >
                    {downloadingReport ? (
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Download Exception Report (.xlsx)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          {step === 1 && (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-200 bg-white rounded-lg text-gray-700 hover:bg-gray-50 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedFile || loading}
                onClick={handlePreview}
                className={`inline-flex items-center px-5 py-2 rounded-lg text-xs font-semibold shadow-sm transition ${
                  selectedFile && !loading
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" />
                    Parsing & Validating...
                  </>
                ) : (
                  <>
                    Preview & Validate File
                    <ArrowRight className="w-3.5 h-3.5 ml-2" />
                  </>
                )}
              </button>
            </>
          )}

          {step === 2 && previewData && (
            <>
              <button
                type="button"
                disabled={loading}
                onClick={() => setStep(1)}
                className="inline-flex items-center px-4 py-2 border border-gray-200 bg-white rounded-lg text-gray-700 hover:bg-gray-50 text-xs font-semibold"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Upload
              </button>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  disabled={validRowsCount === 0 || loading}
                  onClick={handleConfirmImport}
                  className={`inline-flex items-center px-5 py-2 rounded-lg text-xs font-semibold shadow-sm transition ${
                    validRowsCount > 0 && !loading
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" />
                      Importing Policies...
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      Import Valid Policies ({validRowsCount})
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="w-full flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-semibold shadow-sm"
              >
                Close & View Policies
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BulkLICModal;
